import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconButton, Stack } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import api from '../utils/api';
import { fToNow } from '../utils/format-time';

const getId = (item) => item?._id || item?.id || '';

const isExternalLink = (link) => /^https?:\/\//i.test(link || '');

const Notifications = () => {
    const [unreadItems, setUnreadItems] = useState([]);
    const [readItems, setReadItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('unread');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const highlightId = searchParams.get('highlight') || '';
    const highlightRef = useRef(null);

    const load = async () => {
        try {
            setLoading(true);
            const [unreadRes, readRes] = await Promise.all([
                api.get('/notifications', { params: { limit: 50 } }),
                api.get('/notifications/archive', { params: { limit: 50 } }),
            ]);
            const unreadData = Array.isArray(unreadRes?.data) ? unreadRes.data : [];
            const readData = Array.isArray(readRes?.data) ? readRes.data : [];
            const orderedUnread = unreadData.sort((a, b) => new Date(b.createdAt || b._createdAt || 0) - new Date(a.createdAt || a._createdAt || 0));
            const orderedRead = readData.sort((a, b) => new Date((b?._read?.seenAt) || b.createdAt || b._createdAt || 0) - new Date((a?._read?.seenAt) || a.createdAt || a._createdAt || 0));
            setUnreadItems(orderedUnread);
            setReadItems(orderedRead);
        } catch (e) {
            setUnreadItems([]);
            setReadItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (!highlightId || loading) return;
        const inUnread = unreadItems.some((it) => getId(it) === highlightId);
        const inRead = readItems.some((it) => getId(it) === highlightId);
        if (inUnread) setActiveTab('unread');
        else if (inRead) setActiveTab('read');
    }, [highlightId, loading, unreadItems, readItems]);

    useEffect(() => {
        if (!highlightId || loading) return;
        const node = highlightRef.current;
        if (!node) return;
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [highlightId, loading, activeTab]);

    const markSeen = async (id) => {
        if (!id) return;
        try {
            await api.post(`/notifications/${id}/seen`, { clicked: true });
        } catch (_) {
            // ignore
        }
    };

    const handleOpen = async (item) => {
        const id = getId(item);
        await markSeen(id);
        setUnreadItems((prev) => prev.filter((it) => getId(it) !== id));
        setReadItems((prev) => {
            if (prev.some((it) => getId(it) === id)) return prev;
            return [{ ...item, _read: { seenAt: new Date().toISOString(), clicked: true } }, ...prev];
        });

        const link = item?.link;
        if (!link) return;
        if (isExternalLink(link)) {
            window.open(link, '_blank', 'noopener');
            return;
        }
        navigate(link.startsWith('/') ? link : `/${link}`);
    };

    return (
        <div className="main-container container pt-2">
            <div className="row mb-3">
                {/* <div className="col-12 d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">اعلان‌ها</h5>
                    <IconButton size="small" onClick={load} aria-label="refresh" disabled={loading}>
                        <Refresh fontSize="small" />
                    </IconButton>
                </div> */}
                <div className="col-12 mt-3">
                    <div className="  w-100" style={{
                        width: "100%"
                    }} role="group" aria-label="tabs">
                        <button
                            style={{
                                width: "50%"
                            }}
                            type="button"
                            className={`btn ${activeTab === 'unread' ? 'btn-primary' : 'btn-light'}`}
                            onClick={() => setActiveTab('unread')}
                        >
                            خوانده نشده‌ها ({unreadItems.length})
                        </button>
                        <button style={{
                            width: "50%"
                        }}
                            type="button"
                            className={`btn ${activeTab === 'read' ? 'btn-primary' : 'btn-light'}`}
                            onClick={() => setActiveTab('read')}
                        >
                            خوانده شده‌ها (آرشیو) ({readItems.length})
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="row">
                    <div className="col-12 text-center py-4">
                        <span className="text-muted">در حال بارگذاری...</span>
                    </div>
                </div>
            ) : activeTab === 'unread' && unreadItems.length === 0 ? (
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body text-center">
                                <p className="mb-0 text-muted">اعلان جدیدی ندارید.</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : activeTab === 'read' && readItems.length === 0 ? (
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body text-center">
                                <p className="mb-0 text-muted">آرشیو اعلان‌ها خالی است.</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    {(activeTab === 'unread' ? unreadItems : readItems).map((item) => {
                        const itemId = getId(item);
                        const isHighlighted = highlightId && itemId === highlightId;
                        return (
                            <div key={itemId} className="col-12 mb-3" ref={isHighlighted ? highlightRef : null}>
                                <div
                                    className="card"
                                    style={{
                                        cursor: item?.link ? 'pointer' : 'default',
                                        border: isHighlighted ? '2px solid #1976d2' : undefined,
                                        boxShadow: isHighlighted ? '0 0 12px rgba(25, 118, 210, 0.35)' : undefined,
                                        transition: 'border-color 0.3s, box-shadow 0.3s',
                                    }}
                                    onClick={() => handleOpen(item)}
                                >
                                    <div className="card-body">
                                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                            <div style={{ minWidth: 0 }}>
                                                <h6 className="mb-1" style={{ fontWeight: 600 }}>{item?.title || 'اعلان'}</h6>
                                                {(item?.text || item?.message) && (
                                                    <p className="mb-2 text-muted" style={{ fontSize: 13 }}>{item.text || item.message}</p>
                                                )}
                                                <span className="text-secondary" style={{ fontSize: 12 }}>
                                                    {fToNow(item?._read?.seenAt || item?.createdAt || item?._createdAt)}
                                                </span>
                                            </div>
                                        </Stack>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Notifications;
